import { delay, put } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { isEmpty } from 'ramda';

import {
  connectToWebSocketSuccessAction,
  changeDealerSuccessAction,
  saveRoundIdToStore,
  saveGameDataToStore,
  changeGameStateAction,
  changeGameStatusAction,
  saveRoundDataToStore,
  saveRoundsResultsToStore,
  openTableSuccessAction,
  confirmResultSuccessAction,
  bettingTimeStartedAction,
  drawTimeStartedAction,
  changeTimeLeftAction,
  startDecrementTimeLeftAction,
  startIncrementTimeLeftAction,
  drawFinishedAction,
  resultConfirmedAction,
  setCardsToStoreAction,
  setNextCardPositionToStore,
  setIsShuffleRequiredAction,
} from 'data/reducers';
import { getDrawTimeLeft, getTimeLeft } from 'helpers';
import { CardPosition, DealerJoinedResponse, Notifications, TableStates } from 'types';
import { gameStatus } from 'constants/common';
import { setJackpot, setJackpotWon, setStageJackpot } from 'data/reducers/AuthReducer';
import { ACTIONS } from 'data/constants';
import { GameType, TimerTypes } from 'types/tables';

export function* dealerJoined({ type: responseType, data }: DealerJoinedResponse) {
  try {
    if (responseType === Notifications.DEALER_CONNECTED) {
      yield put(connectToWebSocketSuccessAction());

      if (data && !isEmpty(data) && data.configuration) {
        const state = data.status;
        yield put(openTableSuccessAction());
        yield put(changeDealerSuccessAction({ dealer: data.dealer }));
        yield put(saveRoundIdToStore({ roundId: data.round?.roundId || '-' }));
        yield put(saveGameDataToStore({ selectedTableData: data.configuration }));
        yield put(changeGameStateAction({ tableState: state }));

        if (data.configuration.game_type === GameType.BAC) {
          yield put(saveRoundsResultsToStore({ drawResults: data.last_balls || [] }));
        }
        if (data.configuration.game_type === GameType.ROULETTE) {
          yield put(saveRoundsResultsToStore({ drawResults: data.lastWinners?.slice(-5) || [] }));
        }
        if (data.configuration.game_type === GameType.CLASSIC_BAC) {
          if (data.round?.cards) {
            yield put(setCardsToStoreAction(data.round?.cards));
          }
          yield put(setIsShuffleRequiredAction(data.waitingShuffle || false));
        }

        if (data.jackpot) {
          const currentJackpot = data.jackpot;

          if (currentJackpot) {
            yield put(setJackpot(currentJackpot));

            if (currentJackpot?.draw?.stageNumber) {
              yield put(setStageJackpot(true));
            }
          }
        }

        switch (state) {
          case TableStates.TableOpened:
            yield put(changeGameStatusAction({ status: gameStatus.OpenRound }));
            break;
          case TableStates.BettingTimeStarted:
            if (data.round?.finishBettingTime) {
              yield put(
                changeTimeLeftAction({
                  timerLeft: getTimeLeft(
                    data.round.finishBettingTime,
                    data.configuration.betting_time,
                  ),
                }),
              );
              yield put(
                bettingTimeStartedAction({
                  bettingTime: data.configuration.betting_time,
                  finishBettingTime: data.round.finishBettingTime,
                }),
              );
              yield put(startDecrementTimeLeftAction({ type: TimerTypes.bettingTime }));
            }
            yield put(changeGameStatusAction({ status: gameStatus.BettingTime }));
            break;
          case TableStates.BettingTimeFinished:
            yield put(changeGameStatusAction({ status: gameStatus.StartDraw }));
            break;
          case TableStates.DrawStarted:
            if (data.round?.drawStartedAt) {
              yield put(
                changeTimeLeftAction({
                  timerLeft: getDrawTimeLeft(data.round.drawStartedAt),
                }),
              );
              yield put(drawTimeStartedAction({ drawStartedAt: data.round.drawStartedAt }));
            }
            yield put(startIncrementTimeLeftAction());

            if (data.configuration.game_type === GameType.CLASSIC_BAC) {
              if (data.round?.waitingNextCardPosition) {
                yield put(
                  setNextCardPositionToStore(
                    data.round?.waitingNextCardPosition.toString() as CardPosition,
                  ),
                );
              }
            }

            if (data.configuration.game_type === GameType.ROULETTE) {
              yield put(changeGameStatusAction({ status: gameStatus.SpinInProgress }));
              break;
            }
            yield put(changeGameStatusAction({ status: gameStatus.StartedDraw }));
            break;
          case TableStates.DrawFinished:
            yield put(changeGameStatusAction({ status: gameStatus.ConfirmResult }));
            yield put(
              saveRoundDataToStore({
                winner: data?.round?.winner || '',
                drawTime: data.round?.drawTime || 0,
              }),
            );
            if (data.round?.finishDrawnDisplay) {
              yield put(
                changeTimeLeftAction({
                  timerLeft: getTimeLeft(
                    data.round.finishDrawnDisplay,
                    data.configuration.displaying_drawn_result,
                  ),
                }),
              );
              yield put(
                drawFinishedAction({
                  displayingDrawTime: data.configuration.displaying_drawn_result,
                  finishDrawnDisplay: data.round.finishDrawnDisplay,
                }),
              );
              yield put(startDecrementTimeLeftAction({ type: TimerTypes.displayingDrawn }));
            }
            break;
          case TableStates.ResultConfirmed:
            yield put(changeGameStatusAction({ status: gameStatus.DisplayingWin }));
            yield put(confirmResultSuccessAction({ winner: data?.round?.winner || '' }));
            if (data.round?.finishResultDisplay) {
              yield put(
                changeTimeLeftAction({
                  timerLeft: getTimeLeft(
                    data.round?.finishResultDisplay,
                    data.configuration.displaying_win_result,
                  ),
                }),
              );
              yield put(
                resultConfirmedAction({
                  displayingWinTime: data.configuration.displaying_win_result,
                  finishWinDisplay: data.round.finishResultDisplay,
                }),
              );
              yield put(startDecrementTimeLeftAction({ type: TimerTypes.displayingWin }));
            }
            break;
          case TableStates.RoundFinished:
            yield put(setJackpotWon(false));
            yield put(setStageJackpot(false));

            yield put(changeGameStatusAction({ status: gameStatus.OpenRound }));
            break;
          case TableStates.RoundCanceled:
            yield put(changeGameStatusAction({ status: gameStatus.OpenRound }));
            break;
          default:
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error);
  } finally {
    yield delay(3000);

    yield put({ type: ACTIONS.WATCH_DATE_CONTROL_INTERVAL });
  }
}
